// In-app AI Help Assistant — pure helpers (SPEC-v1 §7). No React, no network.

/**
 * Read a File as a base64 data URL (data:image/png;base64,...), the shape the
 * backend `images[]` expects. Rejects on read error.
 */
export const fileToDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error || new Error("read failed"));
    reader.readAsDataURL(file);
  });

/**
 * Map the client-side thread to the API `history` contract: only user/assistant
 * turns, only { role, content }. Screenshots and source chips are dropped
 * (history is text-only; the backend re-clamps to the last turns).
 */
export const toHistory = (messages = []) =>
  messages
    .filter(
      (m) =>
        (m.role === "user" || m.role === "assistant") &&
        typeof m.content === "string" &&
        m.content.trim() !== ""
    )
    .map((m) => ({ role: m.role, content: m.content }));

// Matches the support email or an http(s) URL, for safe (no innerHTML) linkify.
const LINK_RE = /(support@iyuga\.net|https?:\/\/[^\s)]+)/gi;

/**
 * Tokenize a plain-text answer into { type, value } parts so a caller can render
 * links as real <a> nodes while every text part stays a React-escaped text node
 * (no dangerouslySetInnerHTML — avoids the WebView/XSS interpolation trap).
 */
export const linkifyParts = (text = "") => {
  const parts = [];
  let lastIndex = 0;
  let match;
  LINK_RE.lastIndex = 0;
  while ((match = LINK_RE.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push({ type: "text", value: text.slice(lastIndex, match.index) });
    }
    const value = match[0];
    parts.push({
      type: value.includes("@") ? "email" : "url",
      value,
    });
    lastIndex = match.index + value.length;
  }
  if (lastIndex < text.length) {
    parts.push({ type: "text", value: text.slice(lastIndex) });
  }
  return parts;
};

/** True when the OS asks for reduced motion (used to disable panel transitions). */
export const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/**
 * T22.440: where a source chip under an answer goes. The backend cites FAQ rows
 * by their question text (`sources[].title`), so the chip deep-links to the Help
 * page with `?question=`, which opens that article (HelpUnified resolves it).
 */
export const helpSourceHref = (source) => {
  const title = typeof source?.title === "string" ? source.title.trim() : "";
  return title ? `/help?question=${encodeURIComponent(title)}` : "/help";
};
